import type { Method } from 'axios';
import { apiRoutes } from '../@generated/api-routes';

type ApiRoutes = typeof apiRoutes;

export type ApiRouteName = keyof ApiRoutes;

export type ApiRoute = {
  url: string;
  method: Method;
};

type UriParams<Uri extends string> = Uri extends `${string}{${infer Param}}${infer Rest}`
  ? Param | UriParams<Rest>
  : never;
type Params<Name extends ApiRouteName> = UriParams<ApiRoutes[Name]['uri']>;
type RequiredParams<Name extends ApiRouteName> = Exclude<Params<Name>, `${string}?`>;
type OptionalParams<Name extends ApiRouteName> =
  Params<Name> extends infer Param ? (Param extends `${infer Key}?` ? Key : never) : never;

type ParamValue = string | number;

export type ApiRouteParams<Name extends ApiRouteName> = [Params<Name>] extends [never]
  ? Record<string, never>
  : { [Key in RequiredParams<Name>]: ParamValue } & { [Key in OptionalParams<Name>]?: ParamValue };

/**
 * Builds the path and method for a backend API route. Query strings are passed to the
 * request itself, so every parameter here must be part of the route's path.
 */
export function apiRoute<Name extends ApiRouteName>(
  name: Name,
  // An unknown name widens Name to every route; the first branch keeps the error on the name.
  ...params: ApiRouteName extends Name
    ? [params?: never]
    : [RequiredParams<Name>] extends [never]
      ? [params?: ApiRouteParams<Name>]
      : [params: ApiRouteParams<Name>]
): ApiRoute {
  const { method, uri } = apiRoutes[name];
  const values: Record<string, ParamValue | null | undefined> = { ...params[0] };

  const path = uri
    .replace(/\{([^}?]+)(\??)\}/g, (_, key: string, optional: string) => {
      const value = values[key];
      delete values[key];

      if (value === null || value === undefined) {
        if (optional) return '';
        throw new Error(`The '${key}' parameter is required for API route '${name}'.`);
      }

      // Encode the whole value, so a slug from a URL cannot add path segments or a query.
      return encodeURIComponent(`${value}`);
    })
    .replace(/\/+$/, '');

  const unknown = Object.keys(values);
  if (unknown.length) {
    throw new Error(`API route '${name}' has no '${unknown.join("', '")}' parameter.`);
  }

  return { url: `/${path}`, method };
}
